import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  acknowledgeGoal,
  getGoals,
  updateGoal,
} from "../../../../services/goalsApi";
import { formatShortDate, sortActiveGoals } from "../../helpers/goals";
import CombinedLoadRow from "./CombinedLoadRow";
import DeadlineGoalCard from "./DeadlineGoalCard";
import GoalCreateModal from "./GoalCreateModal";
import GoalManageModal from "./GoalManageModal";
import NewGoalRow from "./NewGoalRow";
import RateGoalCard from "./RateGoalCard";
import YearlyGoalCard from "./YearlyGoalCard";

const SectionTitle = ({ children, ...props }) => (
  <Text
    color="gray.500"
    fontSize="sm"
    fontWeight="bold"
    letterSpacing="0.8px"
    textTransform="uppercase"
    {...props}
  >
    {children}
  </Text>
);

const ArchivedRow = ({ goal }) => {
  const completed = goal.status === "completed";
  return (
    <Flex align="flex-start" gap="3" py="3">
      <Box color={completed ? "green.400" : "gray.500"} mt="1">
        {completed ? <CheckIcon boxSize="4" /> : <Text fontSize="sm">—</Text>}
      </Box>
      <Box flex="1" minW="0">
        <Text fontSize="lg" fontWeight="semibold">
          {goal.title}
        </Text>
        <Text color="gray.400" fontSize="sm">
          {goal.summary || (completed ? "Completed" : "Ended")}
          {goal.completedAt ? ` · ${formatShortDate(goal.completedAt)}` : ""}
        </Text>
      </Box>
    </Flex>
  );
};

/**
 * The Goals tab: combined load, active cards, new goal row, archive.
 */
const GoalsTab = ({ createInitial, onCreateHandled }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const createDisclosure = useDisclosure();
  const manageDisclosure = useDisclosure();
  const [managedGoal, setManagedGoal] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const celebratedRef = useRef(new Set());

  const goalsQuery = useQuery(["goals"], getGoals, {
    staleTime: 5 * 60 * 1000,
  });
  const payload = goalsQuery.data?.data || {};
  const active = sortActiveGoals(
    Array.isArray(payload.active) ? payload.active : []
  );
  const archived = Array.isArray(payload.archived) ? payload.archived : [];
  const completedCount = archived.filter(
    (goal) => goal.status === "completed"
  ).length;
  const load = payload.combinedLoad;
  const activeCount = active.filter((goal) => goal.status === "active").length;

  const acknowledgeMutation = useMutation((goalId) => acknowledgeGoal(goalId));
  const updateMutation = useMutation(({ goalId, changes }) =>
    updateGoal(goalId, changes)
  );

  useEffect(() => {
    archived
      .filter(
        (goal) =>
          goal.status === "completed" &&
          !goal.acknowledgedAt &&
          !celebratedRef.current.has(goal.id)
      )
      .forEach((goal) => {
        celebratedRef.current.add(goal.id);
        toast({
          title: `Goal complete · ${goal.summary || goal.title}`,
          status: "success",
          duration: 8000,
          isClosable: true,
        });
        acknowledgeMutation.mutate(goal.id);
      });
    // Celebrate once per goal per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archived]);

  useEffect(() => {
    if (createInitial) {
      createDisclosure.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createInitial]);

  const onManage = (goal) => {
    setManagedGoal(goal);
    manageDisclosure.onOpen();
  };

  const applyChange = (goal, changes, title) =>
    updateMutation
      .mutateAsync({ goalId: goal.id, changes })
      .then(() => {
        queryClient.invalidateQueries(["goals"]);
        toast({ title, status: "success", duration: 4000, isClosable: true });
      })
      .catch((error) =>
        toast({
          title: "Could not update the goal",
          description: error?.response?.data?.message || "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        })
      );

  const onMoveDate = (goal, date) => {
    if (date === null) {
      applyChange(goal, { endsOn: null }, "Deadline removed.");
      return;
    }
    if (date) {
      applyChange(
        goal,
        { endsOn: date },
        `Deadline moved to ${formatShortDate(date)}.`
      );
      return;
    }
    onManage(goal);
  };

  const onUpdateTarget = (goal, target) =>
    applyChange(goal, { targetCount: target }, `Target updated to ${target}.`);

  if (goalsQuery.isLoading) {
    return (
      <Center py="12">
        <Spinner />
      </Center>
    );
  }

  if (goalsQuery.isError) {
    return (
      <Center flexDirection="column" py="12">
        <Text color="gray.400" mb="3">
          Goals could not be loaded.
        </Text>
        <Button
          onClick={() => goalsQuery.refetch()}
          size="sm"
          variant="outline"
        >
          Try again
        </Button>
      </Center>
    );
  }

  return (
    <Box>
      {activeCount >= 2 && !!load && <CombinedLoadRow load={load} />}

      <Box px={{ base: 5, md: 8 }} py="5">
        <SectionTitle mb="3">
          ACTIVE{active.length ? ` · ${active.length}` : ""}
        </SectionTitle>
        {active.length === 0 && (
          <Text color="gray.400" fontSize="sm" mb="4">
            No goal yet. Set one to see whether you are moving fast enough to
            get where you want.
          </Text>
        )}
        {active.map((goal) => {
          if (goal.type === "deadline") {
            return (
              <DeadlineGoalCard
                goal={goal}
                key={goal.id}
                onManage={onManage}
                onMoveDate={onMoveDate}
                onUpdateTarget={onUpdateTarget}
              />
            );
          }
          if (goal.type === "rate") {
            return (
              <RateGoalCard goal={goal} key={goal.id} onManage={onManage} />
            );
          }
          return (
            <YearlyGoalCard goal={goal} key={goal.id} onManage={onManage} />
          );
        })}
        <NewGoalRow onClick={createDisclosure.onOpen} />
      </Box>

      <Box
        borderTop="1px solid"
        borderColor="gray.700"
        px={{ base: 5, md: 8 }}
        py="5"
      >
        <Flex
          align="center"
          as="button"
          justify="space-between"
          onClick={() => setArchiveOpen((value) => !value)}
          type="button"
          w="100%"
          aria-expanded={archiveOpen}
        >
          <SectionTitle>COMPLETED · {completedCount}</SectionTitle>
          {archiveOpen ? (
            <ChevronUpIcon color="gray.500" />
          ) : (
            <ChevronDownIcon color="gray.500" />
          )}
        </Flex>
        {archived.length === 0 && (
          <Text color="gray.400" fontSize="sm" mt="3">
            Goals you reach will appear here with their result.
          </Text>
        )}
        {archived.length > 0 && (
          <Box mt="1">
            {(archiveOpen ? archived : archived.slice(0, 1)).map((goal) => (
              <ArchivedRow goal={goal} key={goal.id} />
            ))}
          </Box>
        )}
      </Box>

      <GoalCreateModal
        initial={createInitial || {}}
        isOpen={createDisclosure.isOpen}
        onClose={() => {
          createDisclosure.onClose();
          onCreateHandled?.();
        }}
      />
      <GoalManageModal
        goal={managedGoal}
        isOpen={manageDisclosure.isOpen}
        onClose={manageDisclosure.onClose}
      />
    </Box>
  );
};

export default GoalsTab;
